import {
  Alert,
  AlertDescription,
  AlertIcon,
  Box,
  Button,
  FormControl,
  FormLabel,
  HStack,
  Select,
  SimpleGrid,
  Stack,
  useDisclosure,
} from "@chakra-ui/react";
import CoversPreview from "@raiden/library/components/Covers/Preview";
import FormGrid from "@raiden/library/components/Form/FormGrid";
import HoldersDietSelect from "@raiden/library/components/Form/HoldersDietSelet";
import RadioBox from "@raiden/library/components/Form/RadioBox";
import RadioGroupRaiden from "@raiden/library/components/Form/RadioGroupRaiden";
import FormController from "@raiden/library/components/ReactHookForm/FormController";
import FormObserver from "@raiden/library/components/ReactHookForm/FormObserver";
import ObserverRHF from "@raiden/library/components/ReactHookForm/ObserverRHF";
import {
  HOLDERS_GENDERS_LIST,
  HOLDERS_TYPES_LIST,
  HOLDERS_TYPES_VALUE_CHILD,
} from "@raiden/library/constants/holders";
import { RESOURCES_COVERIZABLE_OBJECT_TYPE_VALUE_HOLDERS } from "@raiden/library/constants/resources";
import CoverModal from "@raiden/library/containers/Covers/CoverModal";
import capitalize from "@splitfire-agency/raiden-library/dist/libraries/utils/capitalize";
import { useIntl } from "react-intl";
import ApiErrorMessage from "../../../components/ApiErrorMessage";
import ContentBox from "../../../components/ContentBox";

/**
 * @typedef {object} Props
 * @property {"create" | "update"} context
 * @property {boolean} isLoading
 *
 * @param {import("react").PropsWithChildren<Props>} props
 */
function HoldersFormGeneralInformation({ context, isLoading, children }) {
  const intl = useIntl();

  const { isOpen, onOpen, onClose } = useDisclosure();

  return (
    <>
      <CoverModal
        isLoading={isLoading}
        isOpen={isOpen}
        onClose={onClose}
        coverName="id_card"
        transformationsName="data.id_card.transformations"
        objectType={RESOURCES_COVERIZABLE_OBJECT_TYPE_VALUE_HOLDERS}
        withLegend={false}
      />

      <ContentBox
        title={intl.formatMessage({
          defaultMessage: "Informations générales",
        })}>
        <Stack spacing="1rem">
          <Box>
            <ApiErrorMessage />

            <Stack spacing="1rem">
              {context === "create" && (
                <>
                  <FormController
                    name="data.type"
                    rules={{ required: true }}
                    renderWithFormControl={(field) => (
                      <RadioGroupRaiden {...field}>
                        <SimpleGrid columns={{ base: 1, lg: 2 }} gap="1rem">
                          {HOLDERS_TYPES_LIST?.map?.((type) => (
                            <RadioBox
                              value={type.value}
                              text={capitalize(intl.formatMessage(type.label))}
                              description={intl.formatMessage(type.description)}
                              key={type.value}
                            />
                          ))}
                        </SimpleGrid>
                      </RadioGroupRaiden>
                    )}
                  />

                  <Alert status="warning">
                    <AlertIcon />

                    <AlertDescription>
                      {intl.formatMessage({
                        defaultMessage:
                          "Attention, le choix de la catégorie d'adhérent est définitif, cela ne pourra pas être modifié après la création.",
                      })}
                    </AlertDescription>
                  </Alert>
                </>
              )}

              <FormGrid>
                <FormController
                  name="data.gender"
                  label={intl.formatMessage({ defaultMessage: "Civilité" })}
                  rules={{ required: true }}
                  renderWithFormControl={(field) => (
                    <Select {...field}>
                      <option value="">-</option>

                      {HOLDERS_GENDERS_LIST?.map?.((gender) => (
                        <option value={gender.value} key={gender.value}>
                          {intl.formatMessage(gender.civility)}
                        </option>
                      ))}
                    </Select>
                  )}
                />
              </FormGrid>

              <FormGrid>
                <FormController
                  name="data.last_name"
                  label={intl.formatMessage({ defaultMessage: "Nom" })}
                  rules={{ required: true }}
                />

                <FormController
                  name="data.first_name"
                  label={intl.formatMessage({ defaultMessage: "Prénom" })}
                  rules={{ required: true }}
                />

                <FormController
                  type="date"
                  name="data.birthdate"
                  label={intl.formatMessage({
                    defaultMessage: "Date de naissance",
                  })}
                  rules={{ required: true }}
                />
              </FormGrid>
            </Stack>
          </Box>

          <FormObserver name="data.type">
            {(type) =>
              type === HOLDERS_TYPES_VALUE_CHILD ? (
                <>
                  <FormGrid>
                    <FormController
                      name="data.diet"
                      label={intl.formatMessage({
                        defaultMessage: "Régime alimentaire",
                      })}
                      renderWithFormControl={(field) => (
                        <HoldersDietSelect {...field} />
                      )}
                    />
                  </FormGrid>

                  <FormController
                    name="data.comment"
                    type="textarea"
                    label={intl.formatMessage({
                      defaultMessage:
                        "Renseignements médicaux concernant l'enfant",
                    })}
                  />
                </>
              ) : null
            }
          </FormObserver>

          <FormGrid>
            <FormControl>
              <FormLabel>
                {intl.formatMessage({ defaultMessage: "Photo d'identité" })}
              </FormLabel>

              <Box>
                <ObserverRHF
                  names={["id_card", "data.id_card.transformations"]}
                  render={({ values: [photo, transformations] }) => (
                    <>
                      {photo ? (
                        <HStack spacing="1rem">
                          <CoversPreview
                            coverImage={photo}
                            width={80}
                            height={112}
                            x={transformations?.default?.x}
                            y={transformations?.default?.y}
                            cropWidth={transformations?.default?.width}
                            cropHeight={transformations?.default?.height}
                          />

                          <Button onClick={onOpen}>
                            {intl.formatMessage({ defaultMessage: "Modifier" })}
                          </Button>
                        </HStack>
                      ) : (
                        <HStack spacing="1rem">
                          <Button
                            onClick={onOpen}
                            variant="outline"
                            colorScheme="brandPrimary">
                            {intl.formatMessage({
                              defaultMessage: "Ajouter une photo",
                            })}
                          </Button>
                        </HStack>
                      )}
                    </>
                  )}
                />
              </Box>
            </FormControl>
          </FormGrid>

          {children && <Box>{children}</Box>}
        </Stack>
      </ContentBox>
    </>
  );
}

export default HoldersFormGeneralInformation;
